import {
	postAuthSendSignInOtp,
	postAuthSignInWithEmailOtp
} from '@repo/api-portal-sdk/gen'
import { Button } from '@repo/ui/button'
import { AuthErrorAlert } from '@repo/ui/modules/auth/components/auth-error-alert'
import { OtpInput } from '@repo/ui/modules/auth/components/otp-input'
import { Spinner } from '@repo/ui/spinner'
import { useMutation } from '@tanstack/react-query'
import { Mail } from 'lucide-react'
import { useEffect, useState } from 'react'
import { resolveAppLanguageCode, useTranslation } from '@/lib/i18n'

export interface SignupOtpState {
	email: string
	emailOtpToken: string
}

export function SignupOtpForm(
	props: SignupOtpState & {
		onBack: () => void
		onRequiresOtp: (state: SignupOtpState) => void
		onSuccess: () => Promise<void>
	}
) {
	const { t, i18n } = useTranslation()
	const language = resolveAppLanguageCode(
		i18n.resolvedLanguage ?? i18n.language
	)
	const [code, setCode] = useState('')
	const [cooldownSeconds, setCooldownSeconds] = useState(60)
	const verify = useMutation({
		mutationFn: () =>
			postAuthSignInWithEmailOtp({
				body: {
					otp: code,
					emailOtpToken: props.emailOtpToken
				}
			}),
		onError: () => setCode(''),
		onSuccess: props.onSuccess
	})
	const resend = useMutation({
		mutationFn: () =>
			postAuthSendSignInOtp({
				body: { email: props.email, rememberMe: false, language }
			}),
		onError: (error) => {
			const retryAfterSeconds = getRetryAfterSeconds(error)
			if (retryAfterSeconds !== undefined) {
				setCooldownSeconds(retryAfterSeconds)
			}
		},
		onSuccess: (response) => {
			props.onRequiresOtp({
				email: props.email,
				emailOtpToken: response.data.emailOtpToken
			})
			setCode('')
			setCooldownSeconds(60)
		}
	})

	useEffect(() => {
		if (cooldownSeconds <= 0) {
			return
		}
		const timeout = window.setTimeout(
			() => setCooldownSeconds((seconds) => Math.max(0, seconds - 1)),
			1000
		)
		return () => window.clearTimeout(timeout)
	}, [cooldownSeconds])

	return (
		<div className='flex flex-col gap-4 text-center'>
			<Mail aria-hidden='true' className='mx-auto size-8 text-primary' />
			<h1 className='font-semibold text-xl'>
				{t('common:auth.verify_email_title')}
			</h1>
			<p className='text-muted-foreground text-sm'>
				{t('common:auth.verify_email_description')}
			</p>
			{verify.isError ? (
				<AuthErrorAlert error={verify.error} onDismiss={verify.reset} />
			) : null}
			{resend.isError ? (
				<AuthErrorAlert error={resend.error} onDismiss={resend.reset} />
			) : null}
			<OtpInput
				disabled={verify.isPending}
				onChange={setCode}
				value={code}
			/>
			<Button
				className='w-full'
				disabled={code.length !== 6 || verify.isPending}
				onClick={() => verify.mutate()}
				type='button'
			>
				{verify.isPending ? (
					<Spinner />
				) : (
					t('common:auth.verify_email_submit')
				)}
			</Button>
			<Button
				disabled={resend.isPending || cooldownSeconds > 0}
				onClick={() => resend.mutate()}
				type='button'
				variant='outline'
			>
				{resend.isPending ? <Spinner /> : null}
				{cooldownSeconds > 0
					? t('common:auth.verify_email_resend_cooldown', {
							seconds: cooldownSeconds
						})
					: t('common:auth.verify_email_resend')}
			</Button>
			<Button onClick={props.onBack} type='button' variant='ghost'>
				{t('common:auth.back_to_signup')}
			</Button>
		</div>
	)
}

function getRetryAfterSeconds(error: unknown) {
	if (
		typeof error === 'object' &&
		error !== null &&
		'retryAfterSeconds' in error &&
		typeof error.retryAfterSeconds === 'number' &&
		Number.isFinite(error.retryAfterSeconds)
	) {
		return Math.max(1, Math.ceil(error.retryAfterSeconds))
	}

	return undefined
}
